import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  findDefaultOptionSet,
  optionSetsByUuid,
  positiveOptionOf,
} from "./templateTree";

const OPERATORS = ["equals", "not_equals"];
const OPERATOR_GLYPH = { equals: "=", not_equals: "≠" };

const selectClass =
  "appearance-none pl-2 pr-7 py-1 text-xs font-semibold text-slate-700 rounded-lg border border-slate-200 bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition cursor-pointer max-w-[16rem] truncate";

// Per-question visibility editor: chips for existing conditions ("1.1 = Yes")
// plus an inline composer — [question] [is / is not] [answer]. The picker only
// offers questions that come earlier in the document (any standard), so loops
// can't be authored directly; conditions whose gate moved later after a
// reorder are flagged amber instead.
export default function QuestionConditions({
  conditions = [],
  earlier,
  preferredDefaultKey,
  labelByKey,
  setByKey,
  optionSets,
  onChange,
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(null); // {dependsOnKey, operator, expectedOption}

  const byUuid = optionSetsByUuid(optionSets);
  const defaultSet = findDefaultOptionSet(optionSets);
  const earlierKeys = new Set(earlier.map((e) => e.key));
  const optionsOf = (questionKey) =>
    (byUuid[setByKey[questionKey]] || defaultSet)?.options || [];
  const optionLabel = (questionKey, optionUuid) => {
    const opt = optionsOf(questionKey).find((o) => o.uuid === optionUuid);
    return opt ? opt.short_label || opt.label : "?";
  };

  if (!earlier.length && !conditions.length) return null;

  const openComposer = () => {
    const dep =
      earlier.find((e) => e.key === preferredDefaultKey) || earlier[earlier.length - 1];
    const positive = positiveOptionOf(byUuid[dep.optionSet] || defaultSet);
    setDraft({
      dependsOnKey: dep.key,
      operator: "equals",
      expectedOption: positive?.uuid || "",
    });
  };

  const setComposerQuestion = (key) => {
    const dep = earlier.find((e) => e.key === key);
    const positive = positiveOptionOf(byUuid[dep?.optionSet] || defaultSet);
    setDraft((prev) => ({
      ...prev,
      dependsOnKey: key,
      expectedOption: positive?.uuid || "",
    }));
  };

  const commitDraft = () => {
    if (!draft?.dependsOnKey || !draft?.expectedOption) return;
    const duplicate = conditions.some(
      (c) =>
        c.dependsOnKey === draft.dependsOnKey &&
        c.operator === draft.operator &&
        c.expectedOption === draft.expectedOption,
    );
    if (!duplicate) {
      onChange([
        ...conditions,
        {
          dependsOnKey: draft.dependsOnKey,
          operator: draft.operator,
          expectedOption: draft.expectedOption,
        },
      ]);
    }
    setDraft(null);
  };

  return (
    <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
      <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider">
        {t("templates.visibleWhen")}
      </span>

      {conditions.map((cond, idx) => {
        const outOfOrder = !earlierKeys.has(cond.dependsOnKey);
        return (
          <span
            key={`${idx}-${cond.dependsOnKey}-${cond.operator}-${cond.expectedOption}`}
            className="inline-flex items-center gap-1.5"
          >
          {idx > 0 && (
            <span className="text-[10px] font-bold text-slate-500 uppercase">
              {t("templates.conditionAnd")}
            </span>
          )}
          <span
            title={
              outOfOrder
                ? t("templates.conditionOutOfOrder")
                : labelByKey[cond.dependsOnKey]
            }
            className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-md border text-[11px] font-bold ${
              outOfOrder
                ? "bg-amber-50 border-amber-300 text-amber-700"
                : "bg-indigo-50 border-indigo-200 text-indigo-700"
            }`}
          >
            {outOfOrder && <span aria-hidden="true">⚠</span>}
            {labelByKey[cond.dependsOnKey] || "?"} {OPERATOR_GLYPH[cond.operator] || "="}{" "}
            {optionLabel(cond.dependsOnKey, cond.expectedOption)}
            <button
              type="button"
              title={t("templates.removeCondition")}
              aria-label={t("templates.removeCondition")}
              onClick={() => onChange(conditions.filter((_, i) => i !== idx))}
              className={`p-0.5 rounded transition hover:text-rose-600 hover:bg-rose-50 ${
                outOfOrder ? "text-amber-500" : "text-indigo-400"
              }`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
          </span>
        );
      })}

      {draft ? (
        <span className="inline-flex items-center gap-1 flex-wrap">
          <select
            value={draft.dependsOnKey}
            onChange={(e) => setComposerQuestion(e.target.value)}
            className={selectClass}
          >
            {earlier.map((e) => (
              <option key={e.key} value={e.key}>
                {e.label}
              </option>
            ))}
          </select>
          <select
            value={draft.operator}
            onChange={(e) => setDraft((prev) => ({ ...prev, operator: e.target.value }))}
            className={selectClass}
          >
            {OPERATORS.map((op) => (
              <option key={op} value={op}>
                {t(op === "equals" ? "templates.conditionIs" : "templates.conditionIsNot")}
              </option>
            ))}
          </select>
          <select
            value={draft.expectedOption}
            onChange={(e) => setDraft((prev) => ({ ...prev, expectedOption: e.target.value }))}
            className={selectClass}
          >
            {optionsOf(draft.dependsOnKey).map((opt) => (
              <option key={opt.uuid} value={opt.uuid}>
                {opt.short_label || opt.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={commitDraft}
            disabled={!draft.expectedOption}
            className="px-2 py-1 rounded-lg bg-indigo-600 text-white text-[11px] font-bold hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("templates.conditionAdd")}
          </button>
          <button
            type="button"
            onClick={() => setDraft(null)}
            className="px-2 py-1 rounded-lg text-[11px] font-bold text-slate-500 hover:bg-slate-100 transition"
          >
            {t("common.cancel")}
          </button>
        </span>
      ) : (
        earlier.length > 0 && (
          <button
            type="button"
            onClick={openComposer}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-dashed border-slate-300 text-[11px] font-bold text-slate-500 hover:border-indigo-400 hover:text-indigo-600 transition"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
            </svg>
            {t("templates.addCondition")}
          </button>
        )
      )}
    </div>
  );
}
